import { Link } from "../components/LocaleLink";
import {
  Search, Handshake, Hammer, CalendarCheck, Building2, ConciergeBell,
  MapPin, MessageSquare, PackageCheck, Settings, KeyRound, LineChart, Sparkles,
  Check, ArrowRight,
} from "lucide-react";
import { useLang } from "../context/LanguageContext";
import { optimizeImg } from "../lib/api";
import { Reveal } from "../components/Reveal";
import { Seo } from "../components/Seo";
import { track } from "../lib/analytics";
import { JsonLd, buildFaqSchema } from "../components/JsonLd";
import {
  Accordion, AccordionContent, AccordionItem, AccordionTrigger,
} from "../components/ui/accordion";

const HERO = "https://images.unsplash.com/photo-1763114298920-eaac2534e739";
const IMG_INTL = "https://images.unsplash.com/photo-1564078516393-cf04bd966897?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzB8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBtb2Rlcm4lMjBhcGFydG1lbnQlMjBpbnRlcmlvciUyMGxpdmluZyUyMHJvb20lMjB3aW5kb3d8ZW58MHx8fHwxNzgyMTk1MjMwfDA&ixlib=rb-4.1.0&q=85";

const SERVICE_ICONS = [Search, Handshake, Hammer, CalendarCheck, Building2, ConciergeBell];
const INTL_ICONS = [MapPin, MessageSquare, PackageCheck, Settings, KeyRound, LineChart, Sparkles];
const overline = "font-accent text-xs sm:text-sm uppercase tracking-[0.25em] text-primary";

export default function PropertyManagement() {
  const { t } = useLang();
  const p = (k) => t(`pmPage.${k}`);
  const services = p("services");
  const intlPoints = p("intlPoints");
  const benefits = p("benefits");
  const faqs = p("faqs");
  const cmp = p("comparison");

  return (
    <div data-testid="property-management-page">
      <Seo title={p("seoTitle")} description={p("seoDescription")} />
      <JsonLd id="pm-faq" data={buildFaqSchema(p("faqs"))} />
      {/* HERO */}
      <section className="relative w-full min-h-[70vh] flex items-end vl-grain overflow-hidden">
        <img src={optimizeImg(HERO, 1920)} alt="French Riviera villa" fetchPriority="high" decoding="async" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/70 to-background/30" />
        <div className="relative px-6 md:px-12 lg:px-24 mx-auto max-w-[1600px] w-full pt-40 pb-20 md:pb-28">
          <Reveal>
            <p className={overline}>{p("overline")}</p>
            <h1 className="mt-5 font-headings text-4xl sm:text-5xl md:text-6xl leading-[1.05] tracking-tight text-foreground max-w-3xl">{p("title")}</h1>
            <p className="mt-8 font-body font-light text-lg text-foreground/80 max-w-2xl leading-relaxed">{p("intro")}</p>
          </Reveal>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24 md:py-32">
        <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-[1600px]">
          <Reveal className="max-w-2xl mb-16">
            <p className={overline}>{p("servicesTitle")}</p>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-border/50">
            {services.map((s, i) => {
              const Icon = SERVICE_ICONS[i] || Check;
              return (
                <Reveal key={i} delay={(i % 3) * 0.1} className="bg-background">
                  <div className="group p-10 h-full hover:bg-card transition-colors duration-500">
                    <Icon size={26} className="text-primary" strokeWidth={1.4} />
                    <h3 className="mt-6 font-headings text-2xl text-foreground">{s.title}</h3>
                    <p className="mt-3 font-body font-light text-foreground/70 leading-relaxed">{s.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* INTERNATIONAL CLIENTS */}
      <section className="bg-secondary border-y border-border/60 py-24 md:py-32">
        <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-[1600px] grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-20">
          <div className="lg:col-span-5">
            <Reveal>
              <p className={overline}>{p("intlTitle")}</p>
              <p className="mt-6 font-headings text-2xl md:text-3xl text-foreground leading-snug">{p("intlIntro")}</p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-10 aspect-[4/3] overflow-hidden">
                <img src={optimizeImg(IMG_INTL, 900)} alt="Managed apartment interior" loading="lazy" decoding="async" className="w-full h-full object-cover" />
              </div>
            </Reveal>
          </div>
          <div className="lg:col-span-7">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-10">
              {intlPoints.map((pt, i) => {
                const Icon = INTL_ICONS[i] || Check;
                return (
                  <Reveal key={i} delay={(i % 2) * 0.08}>
                    <div className="border-t border-primary/40 pt-5">
                      <Icon size={22} className="text-primary" strokeWidth={1.4} />
                      <h3 className="mt-4 font-headings text-xl text-foreground">{pt.title}</h3>
                      <p className="mt-2 font-body font-light text-foreground/70 leading-relaxed text-sm">{pt.desc}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* BENEFITS */}
      <section className="py-24 md:py-32">
        <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-[1600px] grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
          <div className="md:col-span-5">
            <Reveal>
              <h2 className="font-headings text-3xl sm:text-4xl md:text-5xl leading-[1.15] tracking-tight text-foreground">{p("benefitsTitle")}</h2>
            </Reveal>
          </div>
          <div className="md:col-span-7">
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {benefits.map((b, i) => (
                <Reveal key={i} delay={(i % 2) * 0.08}>
                  <li className="flex items-start gap-3 font-body font-light text-foreground/85 border-b border-border/50 pb-4">
                    <Check size={18} className="text-primary shrink-0 mt-1" /> {b}
                  </li>
                </Reveal>
              ))}
            </ul>
            <Reveal delay={0.2}>
              <p className="mt-10 font-headings text-2xl md:text-3xl text-primary leading-snug">{p("benefitsClosing")}</p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Comparison Table — Velmira vs Generic Agency vs Platform */}
      <section
        data-testid="pm-comparison"
        aria-labelledby="pm-comparison-title"
        className="py-24 md:py-32"
      >
        <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-[1200px]">
          <Reveal className="max-w-2xl mb-6">
            <p className={overline}>{cmp.overline}</p>
          </Reveal>
          <Reveal delay={0.05} className="max-w-3xl mb-6">
            <h2
              id="pm-comparison-title"
              className="font-headings text-4xl sm:text-5xl leading-[1.1] tracking-tight text-foreground"
            >
              {cmp.title}
            </h2>
          </Reveal>
          <Reveal delay={0.1} className="max-w-3xl mb-12">
            <p className="font-body font-light text-foreground/70 leading-relaxed text-base md:text-lg">
              {cmp.intro}
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="overflow-x-auto border border-border/60 bg-background">
              <table
                data-testid="pm-comparison-table"
                className="w-full min-w-[720px] border-collapse text-left"
              >
                <caption className="sr-only">{cmp.title}</caption>
                <thead>
                  <tr className="border-b border-border/60 bg-secondary/40">
                    <th scope="col" className="p-5 font-accent text-[11px] uppercase tracking-[0.2em] text-muted-foreground w-[24%]">
                      {cmp.featureLabel || ""}
                    </th>
                    {cmp.columns.map((col, i) => (
                      <th
                        key={i}
                        scope="col"
                        className={
                          "p-5 font-headings text-base md:text-lg " +
                          (i === 0
                            ? "text-primary border-x border-primary/30 bg-primary/5"
                            : "text-foreground/80")
                        }
                      >
                        {col}
                        {i === 0 && (
                          <span
                            aria-hidden
                            className="ml-2 inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.15em] text-primary/70"
                          >
                            <Check size={11} strokeWidth={2.5} />
                            Us
                          </span>
                        )}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {cmp.rows.map((row, ri) => (
                    <tr
                      key={ri}
                      className={
                        "border-b border-border/40 " +
                        (ri % 2 === 1 ? "bg-secondary/20" : "")
                      }
                    >
                      <th
                        scope="row"
                        className="p-5 align-top font-body font-medium text-foreground/85 text-sm md:text-base"
                      >
                        {row.label}
                      </th>
                      {row.values.map((val, vi) => (
                        <td
                          key={vi}
                          className={
                            "p-5 align-top font-body font-light text-sm md:text-base leading-relaxed " +
                            (vi === 0
                              ? "text-foreground border-x border-primary/20 bg-primary/[0.03]"
                              : "text-foreground/60")
                          }
                        >
                          {val}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-6 font-body text-xs text-muted-foreground italic max-w-3xl">
              {cmp.footnote}
            </p>
          </Reveal>
        </div>
      </section>

      {/* BRAND STORY — Velmira Living philosophy */}
      <section
        data-testid="pm-brand-story"
        aria-labelledby="pm-brand-story-title"
        className="bg-secondary border-y border-border/60 py-20 md:py-28"
      >
        <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-[900px] text-center">
          <Reveal>
            <p className={`${overline} justify-center`}>{p("brandStoryOverline")}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <p
              id="pm-brand-story-title"
              className="mt-8 font-headings text-2xl md:text-3xl lg:text-4xl leading-[1.35] tracking-tight text-foreground/90"
            >
              {p("brandStory")}
            </p>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-secondary border-y border-border/60 py-24 md:py-32">
        <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-[1000px]">
          <Reveal className="mb-12">
            <p className={overline}>{p("faqTitle")}</p>
          </Reveal>
          <Reveal delay={0.1}>
            <Accordion type="single" collapsible className="w-full" data-testid="pm-faq">
              {faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-border/60">
                  <AccordionTrigger data-testid={`faq-trigger-${i}`} className="font-headings text-xl md:text-2xl text-foreground hover:text-primary hover:no-underline text-left py-6">
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="font-body font-light text-foreground/70 leading-relaxed text-base pb-6">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 text-center">
        <div className="px-6 md:px-12 lg:px-24 mx-auto max-w-[1600px]">
          <Reveal>
            <h2 className="font-headings text-4xl sm:text-5xl leading-[1.1] tracking-tight text-foreground max-w-2xl mx-auto">{p("ctaTitle")}</h2>
            <p className="mt-5 font-body font-light text-foreground/70 max-w-xl mx-auto">{t("ownerCta.text")}</p>
            <Link to="/owner" data-testid="pm-cta-button" onClick={() => track("property_management_cta_click", { cta_location: "property_management_page", lead_type: "owner" })} className="mt-10 inline-flex items-center gap-2 bg-primary text-primary-foreground hover:bg-primary/90 px-10 py-4 uppercase tracking-[0.2em] text-xs font-body transition-all duration-300">
              {t("ownerCta.button")} <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
